import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { siteData } from '../data/mock';

const ServicePackages = () => {
  const { serviceTiers } = siteData;

  return (
    <section id="services" className="py-20 md:py-32">
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] text-sm font-medium mb-4">
            Service Packages
          </span>
          <h2 className="heading-2 mb-4">
            Choose Your Transformation Path
          </h2>
          <p className="body-large">
            Flexible packages designed for SMEs at every stage of their digital journey.
          </p>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-4 items-stretch">
          {serviceTiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative rounded-2xl p-6 md:p-8 flex flex-col transition-all duration-300 ${
                tier.highlighted
                  ? 'bg-[var(--text-primary)] text-white scale-100 lg:scale-105 shadow-2xl z-10'
                  : 'bg-white border border-black/10 hover:border-[var(--accent-primary)] hover:shadow-lg'
              }`}
            >
              {/* Badge */}
              {tier.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap ${
                    tier.highlighted 
                      ? 'bg-[var(--accent-primary)] text-[var(--text-primary)]'
                      : 'bg-[var(--text-primary)] text-white'
                  }`}>
                    {tier.badge}
                  </span>
                </div>
              )}

              {/* Tier Header */}
              <div className="mb-6 pt-2">
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    tier.highlighted ? 'text-white' : 'text-[var(--text-primary)]'
                  }`}
                >
                  {tier.name}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${
                    tier.highlighted ? 'text-white/70' : 'text-[var(--text-secondary)]'
                  }`}
                >
                  {tier.description}
                </p>
              </div>

              {/* Price */}
              {tier.price && (
                <div className="mb-6">
                  <span
                    className={`text-3xl md:text-4xl font-bold ${
                      tier.highlighted ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'
                    }`}
                  >
                    {tier.price}
                  </span>
                </div>
              )}

              {/* Feature Groups */}
              <div className="flex-1 space-y-5 mb-8">
                {tier.featureGroups.map((group, groupIndex) => (
                  <div key={groupIndex}>
                    {/* Group Title */}
                    <h4
                      className={`text-sm font-bold mb-2 ${
                        tier.highlighted ? 'text-white' : 'text-[var(--text-primary)]'
                      }`}
                    >
                      {group.title}
                    </h4>
                    {/* Group Features */}
                    {group.features.length > 0 && (
                      <ul className="space-y-2">
                        {group.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start gap-2">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                tier.highlighted
                                  ? 'bg-[var(--accent-primary)]'
                                  : 'bg-[var(--accent-wash)]'
                              }`}
                            >
                              <Check
                                className={`w-2.5 h-2.5 ${
                                  tier.highlighted ? 'text-[var(--text-primary)]' : 'text-[var(--accent-text)]'
                                }`}
                                strokeWidth={3}
                              />
                            </div>
                            <span
                              className={`text-sm leading-relaxed ${
                                tier.highlighted ? 'text-white/90' : 'text-[var(--text-body)]'
                              }`}
                            >
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              {/* CTA - Always at bottom */}
              <div className="mt-auto">
                <a
                  href="#contact"
                  className={`block w-full py-3.5 rounded-full font-semibold text-center transition-all duration-200 ${
                    tier.highlighted
                      ? 'bg-[var(--accent-primary)] text-[var(--text-primary)] hover:bg-[var(--accent-strong)]'
                      : 'bg-[var(--text-primary)] text-white hover:bg-[var(--text-primary)]/90'
                  }`}
                >
                  {tier.ctaText}
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Enterprise note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center text-sm text-[var(--text-muted)] mt-8"
        >
          Need a custom solution? <a href="#contact" className="text-[var(--accent-text)] underline underline-offset-2">Contact us</a> for a tailored package.
        </motion.p>
      </div>
    </section>
  );
};

export default ServicePackages;
